import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  Typography,
  Button,
  Table,
  Sheet,
  Input,
  Tabs,
  TabList,
  Tab,
  TabPanel,
  CircularProgress,
  Alert,
  Chip,
} from '@mui/joy';
import {
  DownloadSimple as Download,
  Calendar as CalendarToday,
  Buildings as Business,
} from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { apiAccountingClient } from '../../lib/apiAccounting';

interface VendorSummary {
  vendor: string;
  vendor_id: number | null;
  current: number;
  '1_30': number;
  '31_60': number;
  '61_90': number;
  '91_120': number;
  'over_120': number;
  total: number;
}

interface BillDetail {
  bill_number: string;
  vendor: string;
  vendor_id: number | null;
  bill_date: string;
  due_date: string;
  days_past_due: number;
  total_amount: number;
  paid_amount: number;
  outstanding_amount: number;
}

interface AgingBucket {
  bills: BillDetail[];
  total: number;
  percentage: number;
}

interface APAgingData {
  report_date: string;
  aging_buckets: {
    current: AgingBucket;
    '1_30': AgingBucket;
    '31_60': AgingBucket;
    '61_90': AgingBucket;
    '91_120': AgingBucket;
    'over_120': AgingBucket;
  };
  vendor_summary: VendorSummary[];
  total_outstanding: number;
}

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
};

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

export const APAgingReport: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reportData, setReportData] = useState<APAgingData | null>(null);
  const [asOfDate, setAsOfDate] = useState(new Date().toISOString().split('T')[0]);
  const [activeTab, setActiveTab] = useState(0);

  const fetchReport = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await apiAccountingClient.getAPAgingReport(asOfDate);
      setReportData(response);
    } catch (err) {
      console.error('Failed to fetch A/P aging report:', err);
      setError('Failed to load report. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
  }, [asOfDate]);

  const exportReport = async () => {
    try {
      // For now, we'll create a CSV export
      if (!reportData) return;
      
      let csv = 'Vendor,Current,1-30 Days,31-60 Days,61-90 Days,91-120 Days,Over 120 Days,Total\n';
      
      reportData.vendor_summary.forEach(vendor => {
        csv += `"${vendor.vendor}",${vendor.current},${vendor['1_30']},${vendor['31_60']},${vendor['61_90']},${vendor['91_120']},${vendor['over_120']},${vendor.total}\n`;
      });
      
      csv += `\nTotal,${reportData.aging_buckets.current.total},${reportData.aging_buckets['1_30'].total},${reportData.aging_buckets['31_60'].total},${reportData.aging_buckets['61_90'].total},${reportData.aging_buckets['91_120'].total},${reportData.aging_buckets['over_120'].total},${reportData.total_outstanding}\n`;
      
      // Create and download file
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `AP_Aging_Report_${asOfDate}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to export report:', err);
      setError('Failed to export report');
    }
  };

  const getAgingColor = (bucket: string): 'success' | 'warning' | 'danger' => {
    switch (bucket) {
      case 'current':
        return 'success';
      case '1_30':
      case '31_60':
        return 'warning';
      default:
        return 'danger';
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert color="danger">{error}</Alert>
      </Box>
    );
  }

  if (!reportData) {
    return null;
  }

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 } }}>
      <Box sx={{ mb: 3, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', md: 'center' }, gap: 1.5 }}>
        <div>
          <Typography level="h3" sx={{ mb: 1, fontSize: { xs: '1.25rem', md: undefined } }}>
            Accounts Payable Aging Report
          </Typography>
          <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
            Outstanding payables by age as of {formatDate(asOfDate)}
          </Typography>
        </div>

        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 2 } }}>
          <Input
            type="date"
            value={asOfDate}
            onChange={(e) => setAsOfDate(e.target.value)}
            startDecorator={<CalendarToday />}
          />
          <Button
            variant="outlined"
            color="primary"
            startDecorator={<Download />}
            onClick={exportReport}
          >
            Export CSV
          </Button>
        </Box>
      </Box>

      {/* Summary Cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2, mb: 3 }}>
        <Card>
          <Typography level="body-sm" sx={{ mb: 1 }}>Current</Typography>
          <Typography level="h4" color="success">
            {formatCurrency(reportData.aging_buckets.current.total)}
          </Typography>
          <Typography level="body-xs" sx={{ mt: 0.5 }}>
            {reportData.aging_buckets.current.percentage.toFixed(1)}%
          </Typography>
        </Card>
        
        <Card>
          <Typography level="body-sm" sx={{ mb: 1 }}>1-30 Days</Typography>
          <Typography level="h4" color="warning">
            {formatCurrency(reportData.aging_buckets['1_30'].total)}
          </Typography>
          <Typography level="body-xs" sx={{ mt: 0.5 }}>
            {reportData.aging_buckets['1_30'].percentage.toFixed(1)}%
          </Typography>
        </Card>
        
        <Card>
          <Typography level="body-sm" sx={{ mb: 1 }}>31-60 Days</Typography>
          <Typography level="h4" color="warning">
            {formatCurrency(reportData.aging_buckets['31_60'].total)}
          </Typography>
          <Typography level="body-xs" sx={{ mt: 0.5 }}>
            {reportData.aging_buckets['31_60'].percentage.toFixed(1)}%
          </Typography>
        </Card>
        
        <Card>
          <Typography level="body-sm" sx={{ mb: 1 }}>61-90 Days</Typography>
          <Typography level="h4" color="danger">
            {formatCurrency(reportData.aging_buckets['61_90'].total)}
          </Typography>
          <Typography level="body-xs" sx={{ mt: 0.5 }}>
            {reportData.aging_buckets['61_90'].percentage.toFixed(1)}%
          </Typography>
        </Card>
        
        <Card>
          <Typography level="body-sm" sx={{ mb: 1 }}>91-120 Days</Typography>
          <Typography level="h4" color="danger">
            {formatCurrency(reportData.aging_buckets['91_120'].total)}
          </Typography>
          <Typography level="body-xs" sx={{ mt: 0.5 }}>
            {reportData.aging_buckets['91_120'].percentage.toFixed(1)}%
          </Typography>
        </Card>
        
        <Card>
          <Typography level="body-sm" sx={{ mb: 1 }}>Over 120 Days</Typography>
          <Typography level="h4" color="danger">
            {formatCurrency(reportData.aging_buckets['over_120'].total)}
          </Typography>
          <Typography level="body-xs" sx={{ mt: 0.5 }}>
            {reportData.aging_buckets['over_120'].percentage.toFixed(1)}%
          </Typography>
        </Card>
      </Box>

      {/* Total Outstanding */}
      <Card sx={{ mb: 3, bgcolor: 'primary.50' }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 1 }}>
          <Typography level="title-md">Total Outstanding</Typography>
          <Typography level="h3" color="primary" sx={{ fontSize: { xs: '1.5rem', md: undefined } }}>
            {formatCurrency(reportData.total_outstanding)}
          </Typography>
        </Box>
      </Card>

      {/* Tabs for Summary vs Detail View */}
      <Tabs value={activeTab} onChange={(event, value) => setActiveTab(value as number)}>
        <TabList sx={{ flexWrap: 'wrap', '& > *': { flex: { xs: 1, sm: '0 0 auto' } } }}>
          <Tab>Vendor Summary</Tab>
          <Tab>Detailed Bills</Tab>
        </TabList>
        
        <TabPanel value={0}>
          <Sheet variant="outlined" sx={{ borderRadius: 'sm', overflow: 'auto' }}>
            <Table stickyHeader sx={{ minWidth: 880 }}>
              <thead>
                <tr>
                  <th>Vendor</th>
                  <th style={{ textAlign: 'right' }}>Current</th>
                  <th style={{ textAlign: 'right' }}>1-30</th>
                  <th style={{ textAlign: 'right' }}>31-60</th>
                  <th style={{ textAlign: 'right' }}>61-90</th>
                  <th style={{ textAlign: 'right' }}>91-120</th>
                  <th style={{ textAlign: 'right' }}>Over 120</th>
                  <th style={{ textAlign: 'right' }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {reportData.vendor_summary.map((vendor, index) => (
                  <tr key={index}>
                    <td>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Business size={18} />
                        {vendor.vendor}
                      </Box>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {vendor.current > 0 ? formatCurrency(vendor.current) : '-'}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {vendor['1_30'] > 0 ? formatCurrency(vendor['1_30']) : '-'}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {vendor['31_60'] > 0 ? formatCurrency(vendor['31_60']) : '-'}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {vendor['61_90'] > 0 ? formatCurrency(vendor['61_90']) : '-'}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {vendor['91_120'] > 0 ? formatCurrency(vendor['91_120']) : '-'}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {vendor['over_120'] > 0 ? formatCurrency(vendor['over_120']) : '-'}
                    </td>
                    <td style={{ textAlign: 'right', fontWeight: 'bold' }}>
                      {formatCurrency(vendor.total)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td style={{ fontWeight: 'bold' }}>Total</td>
                  <td style={{ textAlign: 'right', fontWeight: 'bold' }}>
                    {formatCurrency(reportData.aging_buckets.current.total)}
                  </td>
                  <td style={{ textAlign: 'right', fontWeight: 'bold' }}>
                    {formatCurrency(reportData.aging_buckets['1_30'].total)}
                  </td>
                  <td style={{ textAlign: 'right', fontWeight: 'bold' }}>
                    {formatCurrency(reportData.aging_buckets['31_60'].total)}
                  </td>
                  <td style={{ textAlign: 'right', fontWeight: 'bold' }}>
                    {formatCurrency(reportData.aging_buckets['61_90'].total)}
                  </td>
                  <td style={{ textAlign: 'right', fontWeight: 'bold' }}>
                    {formatCurrency(reportData.aging_buckets['91_120'].total)}
                  </td>
                  <td style={{ textAlign: 'right', fontWeight: 'bold' }}>
                    {formatCurrency(reportData.aging_buckets['over_120'].total)}
                  </td>
                  <td style={{ textAlign: 'right', fontWeight: 'bold' }}>
                    {formatCurrency(reportData.total_outstanding)}
                  </td>
                </tr>
              </tfoot>
            </Table>
          </Sheet>
        </TabPanel>
        
        <TabPanel value={1}>
          <Sheet variant="outlined" sx={{ borderRadius: 'sm', overflow: 'auto' }}>
            <Table stickyHeader sx={{ minWidth: 1000 }}>
              <thead>
                <tr>
                  <th>Bill #</th>
                  <th>Vendor</th>
                  <th>Bill Date</th>
                  <th>Due Date</th>
                  <th>Days Past Due</th>
                  <th style={{ textAlign: 'right' }}>Total</th>
                  <th style={{ textAlign: 'right' }}>Paid</th>
                  <th style={{ textAlign: 'right' }}>Outstanding</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(reportData.aging_buckets).flatMap(([bucket, data]) =>
                  data.bills.map((bill, index) => (
                    <tr key={`${bucket}-${index}`}>
                      <td>{bill.bill_number}</td>
                      <td>{bill.vendor}</td>
                      <td>{formatDate(bill.bill_date)}</td>
                      <td>{formatDate(bill.due_date)}</td>
                      <td>{bill.days_past_due}</td>
                      <td style={{ textAlign: 'right' }}>
                        {formatCurrency(bill.total_amount)}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {formatCurrency(bill.paid_amount)}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {formatCurrency(bill.outstanding_amount)}
                      </td>
                      <td>
                        <Chip
                          size="sm"
                          color={getAgingColor(bucket)}
                          variant="soft"
                        >
                          {bucket === 'current' ? 'Current' :
                           bucket === '1_30' ? '1-30 days' :
                           bucket === '31_60' ? '31-60 days' :
                           bucket === '61_90' ? '61-90 days' :
                           bucket === '91_120' ? '91-120 days' :
                           'Over 120 days'}
                        </Chip>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </Sheet>
        </TabPanel>
      </Tabs>
    </Box>
  );
};